/** Step 2: the ticket's modifiers (buyer choices shown as dropdowns). */
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createApolloClient } from "../../../../providers/ApolloClientFactory";
import { userStore } from "../../../../store/user";
import { UPDATE_TICKETING_ITEM } from "../../ticket-items/mutations";
import TicketModifiersEditor from "../../ticket-items/components/TicketModifiersEditor";
import { ensureTokensBulk } from "../../../tokens/registry";
import { TicketModifierGroup, parseTicketModifiers, permutationCount, permutationTokenNames } from "../../../tokens/variantName";
import type { StepProps } from "../TicketingSetupPage";
import { ticketHasSales } from "../types";
import { Card, CheckRow, Chip, StepFooter } from "../ui";

const client = createApolloClient();
const { getState: getUserState } = userStore;

export const ModifiersStep = ({ data, refresh, ticket, ticketId, checks }: StepProps) => {
  const event = data.event!;
  const t = ticket!;
  const [groups, setGroups] = useState<TicketModifierGroup[]>(parseTicketModifiers(t.modifiers));
  const [saving, setSaving] = useState(false);
  useEffect(() => setGroups(parseTicketModifiers(t.modifiers)), [t.id, t.modifiers]);

  const dirty = JSON.stringify(parseTicketModifiers(groups)) !== JSON.stringify(parseTicketModifiers(t.modifiers));

  const save = async () => {
    if (!dirty) return;
    const clean = parseTicketModifiers(groups);
    if (ticketHasSales(t)) {
      const ok = window.confirm(`"${t.name}" has sales. Changing its modifiers changes the scan names of new tickets. Tickets already sold keep their names. Continue?`);
      if (!ok) throw new Error("cancelled");
    }
    setSaving(true);
    try {
      await client.mutate({ mutation: UPDATE_TICKETING_ITEM, variables: { input: { modifiers: clean.length ? clean : null, last_updated_by: getUserState().user?.id }, where: { id: { _eq: t.id } } } });
      if (clean.length) {
        const baseNames = Object.keys((t.token_payload as unknown as Record<string, unknown>) ?? {});
        await ensureTokensBulk(client, event.id, permutationTokenNames(baseNames.length ? baseNames : [t.name], clean));
      }
      await client.clearStore();
      toast.success("Ticket modifiers saved");
      refresh();
    } catch (err) {
      toast.error("Failed to save ticket modifiers: " + err);
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const stepChecks = checks.filter((c) => c.step === "modifiers");

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-4 items-start">
      <div className="flex flex-col gap-3">
        <Card title="Modifiers" subtitle="Optional. A ticket modifier is one choice the buyer makes, shown as a dropdown on the public page. Every combination of choices becomes its own scannable ticket type.">
          {ticketHasSales(t) && <div className="text-xs text-amber-800 bg-amber-50 rounded-md px-3 py-2"><Chip tone="blue">Has sales</Chip> Tickets already sold keep their scan names. New options only affect new sales.</div>}
          <div className="grid grid-cols-6"><TicketModifiersEditor groups={groups} onChange={setGroups} ticketName={t.name} /></div>
          {groups.length > 0 && (
            <div className="bg-gray-50 rounded-md px-3 py-2 text-sm text-gray-700"><b>{permutationCount(groups)} ticket types</b> will be created as these sell. The Gates step picks which gates accept them.</div>
          )}
        </Card>
        <StepFooter ticketId={ticketId} back="details" next="placement" nextLabel={dirty ? "Save and continue to Placement" : "Continue to Placement"} onNext={save} onSave={save} busy={saving} />
      </div>
      <Card title="Checks for this step">
        {stepChecks.length === 0 ? <div className="text-sm text-gray-500">No modifiers on this ticket. That is fine for most tickets.</div> : stepChecks.map((c, i) => <CheckRow key={i} check={c} ticketId={ticketId} />)}
      </Card>
    </div>
  );
};
